import React, { useEffect, useCallback, useState, useMemo } from 'react';
import {
  getDashboardNotifications,
  saveDashboardNotifications,
  getDashboardNotificationsForListing,
  saveDashboardNotificationsForListing,
  saveBuyerNotification,
} from 'api/AppExternalController';
import { ClientContactTag } from '@bb/contacts';
import { ExternalDashboardNotificationsDTO } from 'api/AppExternalController/getDashboardNotifications';
import { message } from 'antd';
import StyledLoading from '@app/react-common/Loading';
import { ExternalAuthContextType } from 'contexts';
import ExternalNotificationBody from './ExternalNotificationBody';
import ExternalNotificationHeader, {
  MY_SHOWINGS,
} from './ExternalNotificationHeader';

const ExternalNotification = ({
  ExternalAuth,
}: {
  ExternalAuth: ExternalAuthContextType;
}) => {
  const { setAuth } = ExternalAuth;

  const {
    dashboardId,
    user,
    listing,
    listings,
    buyerShowingsCount = 0,
  } = ExternalAuth.auth!;

  const hasBuyerShowings =
    buyerShowingsCount > 0 && user?.tags?.includes(ClientContactTag.Buyer);

  const paramListingId = useMemo(() => (
      window.location.hash
        .slice(window.location.hash.indexOf('listingId'))
        .split('=')[1] || ''
    ), []);

  let initialActiveTab: string = paramListingId;
  if (!initialActiveTab) {
    if (hasBuyerShowings) {
      initialActiveTab = MY_SHOWINGS;
    } else {
      // concierge contact only has listing field
      initialActiveTab =
        (listings && listings.length > 0 ? listings![0].id : listing?.id) || '';
    }
  }

  const [activeTab, setActiveTab] = useState(initialActiveTab);

  const [notificationOptions, setNotificationOptions] =
    React.useState<ExternalDashboardNotificationsDTO>();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!dashboardId) return;
    setLoading(true);

    if (activeTab === MY_SHOWINGS) {
      setNotificationOptions({
        notifications: user?.buyerNotifications,
        email: user.email,
        deliveries: {
          Email: user?.buyerEmailDelivery,
        },
      });
    } else {
      const result = await getDashboardNotificationsForListing({
        dashboardId,
        listingId: activeTab,
      });
      setNotificationOptions(result);
    }
    setLoading(false);
  }, [
    dashboardId,
    activeTab,
    user?.buyerNotifications,
    user.email,
    user?.buyerEmailDelivery,
  ]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const saveSettings = async () => {
    setSaving(true);

    try {
      if (activeTab === MY_SHOWINGS) {
        await saveBuyerNotification({
          dashboardId,
          buyerNotifications: notificationOptions.notifications,
          buyerEmailDelivery: notificationOptions.deliveries.Email,
        });
        setAuth({
          ...ExternalAuth.auth,
          user: {
            ...user,
            buyerNotifications: notificationOptions?.notifications,
            buyerEmailDelivery: notificationOptions.deliveries.Email,
          },
        });
      } else {
        await saveDashboardNotificationsForListing({
          dashboardId,
          listingId: activeTab,
          notifications: notificationOptions.notifications,
          email: notificationOptions.email,
          phone: notificationOptions.phone,
          deliveries: notificationOptions.deliveries,
          feedbackCollectedExternal:
            notificationOptions.feedbackCollectedExternal,
        });
      }

      message.success('Settings saved successfully');
    } catch (err) {
      message.error('Error saving settings');
      await fetchNotifications();
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <StyledLoading size="fa-3x" />;
  if (!notificationOptions) return null;

  return (
    <div>
      <ExternalNotificationHeader
        address={listing?.line1}
        listings={listings}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        hasBuyerShowings={hasBuyerShowings}
      />

      <ExternalNotificationBody
        notificationOptions={notificationOptions}
        setNotificationOptions={setNotificationOptions}
        saveSettings={saveSettings}
        saving={saving}
      />
    </div>
  );
};

export default ExternalNotification;
